import palette from "assets/theme/base/palette";
import React, { useEffect, useState, useRef } from "react";
import SubscriptionModal from "./SubscriptionModal";
import * as XLSX from "xlsx";
import Icon from "@mui/material/Icon";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Dialog from "@mui/material/Dialog";
import Stack from "@mui/material/Stack";
import { useMaterialUIController } from "context";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import * as Sentry from "@sentry/browser";
import Loader from "pages/loader/Loader";
import { call } from "services/axiosService/core";
import BulkPayment from "./BulkPayment";
import { useAuthenticator } from "@aws-amplify/ui-react";

/* ─── shared field style ─── */
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#fafafa" } };

/* ─── sub-components defined outside to avoid recreation ─── */
const InstructionList = ({ items, color }) => (
  <Stack spacing={1.5}>
    {items.map((text, i) => (
      <MDBox key={i} display="flex" alignItems="flex-start" gap={1.5}>
        <MDBox sx={{ minWidth: 26, height: 26, borderRadius: "50%", bgcolor: color || palette.blue[700], color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: "bold", flexShrink: 0, mt: 0.2 }}>
          {i + 1}
        </MDBox>
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{text.replace(/^•\s*/, "")}</Typography>
      </MDBox>
    ))}
  </Stack>
);

const BulletList = ({ items }) => (
  <Stack spacing={1}>
    {items.map((text, i) => (
      <MDBox key={i} display="flex" alignItems="flex-start" gap={1.5}>
        <MDBox sx={{ minWidth: 8, height: 8, borderRadius: "50%", bgcolor: palette.orange[500], flexShrink: 0, mt: 0.8 }} />
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{text.replace(/^•\s*/, "")}</Typography>
      </MDBox>
    ))}
  </Stack>
);

const OptionCard = ({ icon, color, bg, label, onClick }) => (
  <Card onClick={onClick} sx={{ p: 2.5, mb: 1.5, borderRadius: "16px", cursor: "pointer", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", transition: "all 200ms", "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,0.12)", transform: "translateY(-2px)" } }}>
    <MDBox display="flex" alignItems="center" gap={2}>
      <MDBox sx={{ width: 44, height: 44, borderRadius: "12px", bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ color, fontSize: "22px !important" }}>{icon}</Icon>
      </MDBox>
      <MDTypography variant="body1" fontWeight="bold" sx={{ flex: 1 }}>{label}</MDTypography>
      <Icon sx={{ color: "text.disabled", fontSize: "20px !important" }}>chevron_right</Icon>
    </MDBox>
  </Card>
);

const StepHeader = ({ icon, iconColor, iconBg, title, subtitle }) => (
  <MDBox textAlign="center" mb={3}>
    <MDBox sx={{ width: 80, height: 80, borderRadius: "50%", bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
      <Icon sx={{ fontSize: "40px !important", color: iconColor }}>{icon}</Icon>
    </MDBox>
    <MDTypography variant="h5" fontWeight="bold" mb={subtitle ? 0.5 : 0}>{title}</MDTypography>
    {subtitle && <MDTypography variant="body2" color="text.secondary">{subtitle}</MDTypography>}
  </MDBox>
);

/* ─── Main component ─── */
const BulkUpload = ({
  adding,
  trueMindsUser,
  setOpenAddAthleteModal,
  setOpenAddChildModal,
  handleCloseBulkUploadModal,
  getChildrens,
  updateUserProfile,
  setOpenSnackbar,
  setAlertMessage,
  setSeverity,
  setProfile,
  t,
}) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading]                 = useState(false);
  const [noPaymentRequired, setNoPaymentRequired] = useState(false);
  const [previewData, setPreviewData]             = useState([]);
  const [duplicateEmails, setDuplicateEmails]     = useState([]);
  const [step, setStep]                           = useState(0);
  const [noLearners, setNoLearners]               = useState(false);
  const [learnerDetails, setLearnerDetails]       = useState([]);
  const [existingLearners, setExistingLearners]   = useState([]);
  const [parentAccounts, setParentAccounts]       = useState([]);
  const [amountToProcess, setAmountToProcess]     = useState(0);
  const [averageCost, setAverageCost]             = useState(0);
  const [subscriptionType, setSubscriptionType]   = useState("maxu_yearly");
  const [termsConditionsChecked, setTermsConditionsChecked] = useState(false);
  const [paymentTermsChecked, setPaymentTermsChecked]       = useState(false);
  const [manualLearners, setManualLearners] = useState([
    { first_name: "", last_name: "", email: "", gender: "", dob: "", code: "", parent_email: "" },
  ]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate]     = useState("");
  const [controller]              = useMaterialUIController();
  const { profile }               = controller;
  const { signOut }               = useAuthenticator((context) => [context.user]);

  const handleSignupChild = async () => { await signOut(); };

  useEffect(() => {
    const today    = new Date();
    const end_date = new Date(today.getFullYear() - 8, today.getMonth(), today.getDate(), today.getHours(), today.getMinutes(), today.getSeconds());
    setEndDate(end_date.toISOString().slice(0, 10));
    if (adding === "asParent") {
      const start_date = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate() + 1, today.getHours(), today.getMinutes(), today.getSeconds());
      setStartDate(start_date.toISOString().slice(0, 10));
    }
  }, []);

  const handleCloseSubscriptionModal = (sub) => { setSubscriptionType(sub); setStep(2); };
  const termsConditionsCheckboxHandler = () => setTermsConditionsChecked((v) => !v);
  const paymentTermsCheckboxHandler    = () => setPaymentTermsChecked((v) => !v);

  const handleManualChange = (index, field, value) => {
    const updated = [...manualLearners];
    updated[index][field] = value;
    setManualLearners(updated);
  };
  const addManualRow = () => setManualLearners([...manualLearners, { first_name: "", last_name: "", email: "", gender: "", dob: "", code: "", parent_email: "" }]);

  const handleDobChange = (index, value) => {
    const updated = [...manualLearners];
    updated[index].dob = value;
    updated[index].email = "";
    updated[index].parent_email = "";
    setManualLearners(updated);
  };

  const getLearnerAge = (dob) => {
    if (!dob) return null;
    return Math.abs(new Date(Date.now() - new Date(dob).getTime()).getUTCFullYear() - 1970);
  };

  const removeManualRow = (index) => {
    if (manualLearners.length > 1) {
      const updated = [...manualLearners];
      updated.splice(index, 1);
      setManualLearners(updated);
    }
  };

  const handleManualSubmit = async () => {
    const baseRequired = ["first_name", "last_name", "gender", "dob"];
    if (manualLearners.some((l) => baseRequired.some((f) => !l[f] || l[f].trim() === ""))) {
      setAlertMessage(t("alerts.bulkUpload.allRequired")); setSeverity("error"); setOpenSnackbar(true); return;
    }
    const hasMissingEmail = manualLearners.some((learner) => {
      if (adding === "asCoach") {
        const age = getLearnerAge(learner.dob);
        if (age !== null && age < 18) return !learner.email?.trim() && !learner.parent_email?.trim();
        return !learner.email?.trim();
      }
      return !learner.email?.trim();
    });
    if (hasMissingEmail) { setAlertMessage(t("alerts.bulkUpload.allRequired")); setSeverity("error"); setOpenSnackbar(true); return; }
    if (adding === "asCoach") {
      const hasParentEmailForAdult = manualLearners.some((l) => { const age = getLearnerAge(l.dob); return age !== null && age >= 18 && l.parent_email?.trim(); });
      if (hasParentEmailForAdult) { setAlertMessage(t("alerts.bulkUpload.parentEmailForAdult") || "Parent email is not allowed for adult athletes (18+)."); setSeverity("error"); setOpenSnackbar(true); return; }
    }
    const hasEmailConflict = manualLearners.some((l) => l.email?.trim() && l.parent_email?.trim() && l.email.trim().toLowerCase() === l.parent_email.trim().toLowerCase());
    if (hasEmailConflict) { setAlertMessage(t("alerts.bulkUpload.emailConflict") || "Learner email and parent email must be different."); setSeverity("error"); setOpenSnackbar(true); return; }
    const emailRegex = /\S+@\S+\.\S+/;
    const hasInvalidEmail = manualLearners.some((l) => (l.email && !emailRegex.test(l.email)) || (adding === "asCoach" && l.parent_email && !emailRegex.test(l.parent_email)));
    if (hasInvalidEmail) { setAlertMessage(t("alerts.bulkUpload.enterEmail")); setSeverity("error"); setOpenSnackbar(true); return; }
    const learnerEmailList  = manualLearners.map((l) => l.email?.trim().toLowerCase()).filter(Boolean);
    const learnerEmailCounts = learnerEmailList.reduce((acc, email) => { acc[email] = (acc[email] || 0) + 1; return acc; }, {});
    if (Object.values(learnerEmailCounts).some((count) => count > 1)) { setAlertMessage(t("alerts.bulkUpload.duplicateEmail")); setSeverity("error"); setOpenSnackbar(true); return; }
    if (adding === "asCoach") {
      const sharedParentEmails = manualLearners.filter((l) => !l.email?.trim() && l.parent_email?.trim()).map((l) => l.parent_email.trim().toLowerCase());
      if (sharedParentEmails.length > 0) {
        const allEmailsFlat = manualLearners.flatMap((l) => [l.email?.trim().toLowerCase() || null, l.parent_email?.trim().toLowerCase() || null]).filter(Boolean);
        if (sharedParentEmails.some((e) => allEmailsFlat.filter((x) => x === e).length > 1)) {
          setAlertMessage(t("alerts.bulkUpload.sharedParentEmailConflict") || "A shared account parent email cannot be reused in other rows."); setSeverity("error"); setOpenSnackbar(true); return;
        }
      }
    }
    if (!termsConditionsChecked || !paymentTermsChecked) { setAlertMessage(t("alerts.bulkUpload.acceptTerms")); setSeverity("error"); setOpenSnackbar(true); return; }
    autoSubmit(manualLearners);
  };

  const handleUpload = (event) => {
    setUploading(true); setPreviewData([]); setDuplicateEmails([]);
    const file = event.target.files[0];
    if (!file) return;
    setTimeout(() => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          let invalidDate = false, invalidDateRange = false, parentEmailForAdult = false, duplicateEmail = false;
          const sameRowConflicts = [];
          const xlsxHeaders = adding === "asCoach"
            ? ["first_name", "last_name", "gender", "dob", "email", "parent_email", "code"]
            : ["first_name", "last_name", "gender", "dob", "email", "code"];
          let rawJson = XLSX.utils.sheet_to_json(worksheet, { header: xlsxHeaders, range: 1, raw: false, defval: "" });
          rawJson = rawJson.slice(0, 100);
          let missingRequiredField = false, invalidGender = false;
          const validGenders = ["Male", "Female"];
          const validLearners = rawJson.map((learner, rowIndex) => {
            const sheetRow = rowIndex + 2;
            const dob = learner.dob, code = learner.code.trim(), gender = learner.gender.trim();
            const first_name = learner.first_name.trim(), last_name = learner.last_name.trim();
            const email = learner.email.trim();
            const parent_email = adding === "asCoach" ? learner.parent_email?.trim() || "" : "";
            if (!first_name || !last_name || !gender || !dob.trim()) { missingRequiredField = true; return null; }
            if (adding === "asCoach" && !email && !parent_email) { missingRequiredField = true; return null; }
            if (adding !== "asCoach" && !email) { missingRequiredField = true; return null; }
            if (!validGenders.includes(gender)) { invalidGender = true; return null; }
            const [month, day, year] = dob.trim().split("/").map((part) => part.trim());
            if (!month || !day || !year) { invalidDate = true; return null; }
            if (!/^\d{1,2}$/.test(month) || !/^\d{1,2}$/.test(day) || !/^\d{4}$/.test(year)) { invalidDate = true; return null; }
            const dobIso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
            const birthDate = new Date(`${dobIso}T00:00:00Z`);
            if (isNaN(birthDate.getTime()) || birthDate.toISOString().slice(0, 10) !== dobIso) { invalidDate = true; return null; }
            const user_age = Math.abs(new Date(Date.now() - birthDate.getTime()).getUTCFullYear() - 1970);
            if ((adding === "asCoach" && user_age < 8) || (adding === "asParent" && (user_age < 8 || user_age > 18))) { invalidDateRange = true; return null; }
            if (adding === "asCoach" && user_age >= 18 && parent_email) { parentEmailForAdult = true; return null; }
            if (adding === "asCoach" && parent_email && email && parent_email.trim().toLowerCase() === email.trim().toLowerCase()) {
              duplicateEmail = true;
              sameRowConflicts.push({ email: email.trim().toLowerCase(), rows: [sheetRow], reason: "sameRow" });
            }
            return { ...learner, first_name, last_name, email, gender, dob: birthDate.toISOString().slice(0, 10), code, parent_email, dob_display: learner.dob, user_age, sheetRow };
          }).filter(Boolean);

          if (missingRequiredField) { setAlertMessage(t("alerts.bulkUpload.allRequired")); setSeverity("error"); setOpenSnackbar(true); return; }
          if (invalidGender)        { setAlertMessage(t("alerts.bulkUpload.invalidGender")); setSeverity("error"); setOpenSnackbar(true); return; }
          if (invalidDate)          { setAlertMessage(t("alerts.bulkUpload.invalidDOB")); setSeverity("error"); setOpenSnackbar(true); return; }
          if (invalidDateRange)     { setAlertMessage(t("alerts.bulkUpload.invalidAge")); setSeverity("error"); setOpenSnackbar(true); return; }
          if (parentEmailForAdult)  { setAlertMessage(t("alerts.bulkUpload.parentEmailForAdult") || "Parent email is not allowed for adult athletes (18+)."); setSeverity("error"); setOpenSnackbar(true); return; }
          if (duplicateEmail)       { setDuplicateEmails(sameRowConflicts); setAlertMessage(t("alerts.bulkUpload.duplicateEmailInFile")); setSeverity("error"); setOpenSnackbar(true); return; }

          const emailRegex = /^\S+@\S+\.\S+$/;
          if (!validLearners.every((l) => (!l.email || emailRegex.test(l.email)) && (adding !== "asCoach" || !l.parent_email || emailRegex.test(l.parent_email)))) {
            setAlertMessage(t("alerts.bulkUpload.invalidEmail")); setSeverity("error"); setOpenSnackbar(true); return;
          }
          const emailMap = {};
          for (const l of validLearners) {
            if (l.email?.trim()) {
              const e = l.email.trim().toLowerCase();
              (emailMap[e] = emailMap[e] || []).push(l.sheetRow);
            }
          }
          const repeatedEmails = Object.entries(emailMap)
            .filter(([, rows]) => rows.length > 1)
            .map(([email, rows]) => ({ email, rows, reason: "repeated" }));
          if (repeatedEmails.length > 0) {
            setDuplicateEmails(repeatedEmails);
            setAlertMessage(t("alerts.bulkUpload.duplicateEmailInFile")); setSeverity("error"); setOpenSnackbar(true); return;
          }
          if (adding === "asCoach") {
            const sharedParentEmails = [...new Set(validLearners.filter((l) => !l.email?.trim() && l.parent_email?.trim()).map((l) => l.parent_email.trim().toLowerCase()))];
            if (sharedParentEmails.length > 0) {
              const allEmailsFlat = validLearners.flatMap((l) => [l.email?.trim().toLowerCase() || null, l.parent_email?.trim().toLowerCase() || null]).filter(Boolean);
              const sharedConflicts = sharedParentEmails
                .filter((e) => allEmailsFlat.filter((x) => x === e).length > 1)
                .map((e) => ({
                  email: e,
                  rows: validLearners
                    .filter((l) => l.email?.trim().toLowerCase() === e || l.parent_email?.trim().toLowerCase() === e)
                    .map((l) => l.sheetRow),
                  reason: "sharedParent",
                }));
              if (sharedConflicts.length > 0) {
                setDuplicateEmails(sharedConflicts);
                setAlertMessage(t("alerts.bulkUpload.sharedParentEmailConflict") || "A shared account parent email cannot be reused in other rows."); setSeverity("error"); setOpenSnackbar(true); return;
              }
            }
          }
          setPreviewData(validLearners);
        } catch (err) {
          setAlertMessage(t("alerts.bulkUpload.errorFile")); setSeverity("error"); setOpenSnackbar(true);
        } finally { setUploading(false); }
      };
      reader.readAsArrayBuffer(file);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }, 0);
  };

  const handleSubmit = async (e) => { e.preventDefault(); autoSubmit(previewData); };

  const autoSubmit = async (data) => {
    try {
      const learners = [];
      data.forEach((learner) => {
        const name = learner.first_name.replace(/\s+/g, "_") + " " + learner.last_name.replace(/\s+/g, "_");
        const birthDate = new Date(learner.dob);
        if (isNaN(birthDate.getTime())) return null;
        const user_age = Math.abs(new Date(Date.now() - birthDate.getTime()).getUTCFullYear() - 1970);
        const age = learner.user_age ? learner.user_age : user_age;
        const code = learner.code, learnerEmail = learner.email?.trim() || "";
        const parentEmail = adding === "asCoach" ? learner.parent_email?.trim() || "" : "";
        let relationships = { parents: [profile.id] };
        let account_type = null, emailForRecord = learnerEmail;
        let type = age < 18 ? ["Learner"] : ["Learner", "Adult"];
        let details = { name, dob: learner.dob, persona: ["Athlete"], gender: learner.gender, address: { street: null, city: null, state: null, country: null, zip: null } };
        if (adding === "asCoach") {
          relationships = age < 18 ? { coaches: [profile.id] } : { coaches: [profile.id], children: [] };
          if (age < 18 && parentEmail && learnerEmail) { account_type = "separate"; }
          else if (age < 18 && parentEmail && !learnerEmail) {
            account_type = "shared"; type = ["Learner", "Shared"]; emailForRecord = parentEmail;
            details = { name: null, dob: null, gender: null, persona: ["Parent"], address: { street: null, city: null, state: null, country: null, zip: null }, learner_details: { name, dob: learner.dob, persona: ["Athlete"], gender: learner.gender } };
          }
        }
        learners.push({
          email: emailForRecord, ...(account_type === "separate" ? { parent_email: parentEmail } : {}),
          account_type, phone: null, relationships, cognito_user_id: null, type, language: profile.language || "en",
          is_demo_user: false, status: "inactive", true_mindsets: !!profile.true_mindsets,
          source: profile.source ? profile.source : (code && code.includes("_") ? code.split("_")[0] + "_code" : null),
          details, payment_status: "pending", profile_image_url: null,
          referral_code: code ? code : null,
          identities: [{ google: { id: "" }, facebook: { id: "" }, apple: { id: "" }, amazon: { id: "" }, email: { id: "" } }],
        });
      });
      setPreviewData([]); setStep(10);
      const response = await call("users/bulk-upload", "POST", { body: JSON.stringify({ learners, added_as: adding, guardian_id: profile.id, subscription_type: subscriptionType }), headers: { "Content-Type": "application/json" } });
      if (response.status === 200) {
        const hasSummary = Array.isArray(response.data.summary) && response.data.summary.length > 0;
        setNoLearners(!hasSummary);
        setAlertMessage(hasSummary ? t("alerts.bulkUpload.success") : t("alerts.bulkUpload.error"));
        setSeverity(hasSummary ? "success" : "error"); setOpenSnackbar(true);
        setLearnerDetails(response.data.summary); setExistingLearners(response.data.existing_learners); setParentAccounts(response.data.parent_accounts || []);
        getChildrens(); updateUserProfile();
        if (response.data.total_amount > 0) { setAverageCost((response.data.total_amount / response.data.summary.length).toFixed(2)); setAmountToProcess(response.data.total_amount); }
        else { setAverageCost(0); setAmountToProcess(0); setNoPaymentRequired(true); }
        setStep(5); setPreviewData([]);
      } else if (response.status !== 440) { setPreviewData([]); setAlertMessage(t("alerts.bulkUpload.error")); setSeverity("error"); setOpenSnackbar(true); }
      else { setPreviewData([]); }
    } catch (err) { setUploading(false); setAlertMessage(t("alerts.bulkUpload.error")); setSeverity("error"); setOpenSnackbar(true); }
    finally { setUploading(false); }
  };

  const proceedToPayment = async () => {
    if (noLearners) { handleCloseBulkUploadModal(); return; }
    if (noPaymentRequired) {
      setStep(10);
      const usages = learnerDetails.filter((l) => l.code_status === "valid_code" && l.referral_code).map((l) => ({ referral_code: l.referral_code, email: l.email }));
      const learner_ids = learnerDetails.map((l) => l.learner_id);
      const AdultDetails = profile;
      let true_users = profile.true_mindsets_users ? profile.true_mindsets_users : [];
      if (["true_mindsets_yearly", "true_mindsets_monthly"].includes(subscriptionType)) {
        true_users = Array.from(new Set([...true_users, ...learner_ids]));
        AdultDetails.true_mindsets_users = true_users;
        call(`users/${profile.id}`, "PUT", { body: AdultDetails, headers: { "Content-Type": "application/json" } }).then((r) => { if (r.status === 200) updateUserProfile(); }).catch((e) => Sentry.captureException(e));
      }
      if (profile.is_demo_user === false) {
        profile.is_demo_user = true;
        call(`users/${profile.id}`, "PUT", { body: profile, headers: { "Content-Type": "application/json" } }).then((r) => { if (r.status === 200) setProfile(profile); }).catch((e) => Sentry.captureException(e));
      }
      if (usages.length > 0) call(`/payments/update-bulk-coupon-usage`, "PUT", { body: { usages } }).catch((e) => Sentry.captureException(e));
      call(`/users/bulk-upload/update-status`, "POST", { body: { learner_details: learnerDetails, guardian_id: profile.id, payment_status: "success", paid_at: new Date(), subscription_type: subscriptionType } })
        .then((r) => { if (r.status === 200) { getChildrens(); setStep(9); } })
        .catch((e) => Sentry.captureException(e));
    } else { setStep(6); }
  };

  /* ──────────── shared terms checkboxes render ──────────── */
  const renderTerms = () => (
    <MDBox sx={{ bgcolor: palette.neutral[50], border: `1px solid ${palette.neutral[200]}`, borderRadius: "12px", p: 2, mb: 3 }}>
      <FormGroup>
        <FormControlLabel
          control={<Checkbox required size="small" checked={termsConditionsChecked} onChange={termsConditionsCheckboxHandler} />}
          label={<MDTypography variant="caption"><span style={{ color: "#f44336" }}>* </span>{t("formLabels.termsAndConditions")}{" "}<Typography component="a" fontSize={12} sx={{ color: palette.brand[500] }} href="/terms_conditions" target="_blank">{t("formLabels.terms")}</Typography></MDTypography>}
        />
        <FormControlLabel
          control={<Checkbox required size="small" checked={paymentTermsChecked} onChange={paymentTermsCheckboxHandler} />}
          label={<MDTypography variant="caption"><span style={{ color: "#f44336" }}>* </span>{t("formLabels.termsAndConditions")}{" "}<Typography component="a" fontSize={12} sx={{ color: palette.brand[500] }} href="/payment_terms" target="_blank">{t("formLabels.paymentTerms")}</Typography></MDTypography>}
        />
      </FormGroup>
    </MDBox>
  );

  /* ──────────── coach rules box ──────────── */
  const renderCoachRules = (withColumnsRule = true) => adding === "asCoach" && (
    <MDBox mt={3} sx={{ bgcolor: palette.amber[50], borderRadius: "12px", p: 2 }}>
      <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
        <Icon sx={{ color: palette.amber[800], fontSize: "18px !important" }}>info</Icon>
        <MDTypography variant="caption" fontWeight="bold" sx={{ color: palette.amber[900], textTransform: "uppercase" }}>
          COACH — EMAIL RULES
        </MDTypography>
      </MDBox>
      <BulletList items={[
        ...(withColumnsRule ? [t("bulkUpload.instructions.instruction_coach_columns")] : []),
        t("bulkUpload.instructions.instruction_coach_minor"),
        t("bulkUpload.instructions.instruction_coach_adult"),
        t("bulkUpload.instructions.instruction_coach_parent_email"),
      ]} />
    </MDBox>
  );

  /* ──────────────────────────────────────────────────────── */
  /*  RENDER                                                  */
  /* ──────────────────────────────────────────────────────── */
  return (
    <Dialog
      open={true}
      maxWidth={false}
      PaperProps={{ sx: { width: "80vw", maxWidth: "1000px", maxHeight: "95vh", borderRadius: "24px", overflow: "hidden", display: "flex", flexDirection: "column" } }}
    >
      {/* Circle close */}
      <MDBox
        onClick={handleCloseBulkUploadModal}
        sx={{
          position: "absolute", top: 12, right: 12, zIndex: 10,
          width: 36, height: 36, borderRadius: "50%",
          bgcolor: palette.neutral[100], border: `1px solid ${palette.neutral[200]}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", transition: "all 180ms ease",
          "&:hover": { bgcolor: palette.neutral[200] },
        }}
      >
        <Icon sx={{ fontSize: "18px !important", color: palette.neutral[600] }}>close</Icon>
      </MDBox>

      {/* ── Step 0: how to add ── */}
      {step === 0 && (
        <MDBox sx={{ px: 4, pt: 5, pb: 4 }}>
          <StepHeader icon="group_add" iconColor={palette.brand[500]} iconBg={`${palette.brand[500]}18`} title={t("bulkUpload.headings.addChildren")} />
          <OptionCard icon="person_add" color={palette.brand[500]} bg={`${palette.brand[500]}18`} label={t("buttonTitles.addOneChild")}
            onClick={() => { if (adding === "asParent") setOpenAddChildModal(true); else setOpenAddAthleteModal(true); handleCloseBulkUploadModal(); }}
          />
          <OptionCard icon="people_alt" color="#4caf50" bg="#4caf5018" label={t("buttonTitles.addMultipleChildren")}
            onClick={() => { trueMindsUser ? setStep(1) : setStep(2); }}
          />
        </MDBox>
      )}

      {/* ── Step 1: subscription selection ── */}
      {step === 1 && (
        <MDBox sx={{ p: 3 }}>
          <SubscriptionModal isOpen={true} onClose={handleCloseSubscriptionModal} />
        </MDBox>
      )}

      {/* ── Step 2: manual vs bulk ── */}
      {step === 2 && (
        <MDBox sx={{ px: 4, pt: 5, pb: 4 }}>
          <StepHeader icon="people_alt" iconColor="#4caf50" iconBg="#4caf5018" title={t("bulkUpload.headings.addChildren")} />
          <OptionCard icon="edit_note" color={palette.brand[500]} bg={`${palette.brand[500]}18`} label={t("buttonTitles.addChildrenManually")} onClick={() => setStep(12)} />
          <OptionCard icon="upload_file" color="#ff9800" bg="#ff980018" label={t("buttonTitles.bulkUpload")} onClick={() => setStep(11)} />
        </MDBox>
      )}

      {/* ── Step 11: bulk upload instructions ── */}
      {step === 11 && (
        <MDBox sx={{ px: 4, pt: 5, pb: 4, display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
          {/* pinned header */}
          <MDBox sx={{ flexShrink: 0 }}>
            <StepHeader icon="upload_file" iconColor="#ff9800" iconBg="#ff980018" title={t("headings.addLearner")} subtitle={adding === "asParent" ? t("messages.profile.addAsParent") : t("messages.profile.addAsCoach")} />
          </MDBox>
          {/* scrollable instruction content */}
          <MDBox sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
            <MDBox sx={{ bgcolor: palette.brand[50], borderRadius: "16px", p: 3 }}>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <Icon sx={{ color: palette.blue[700], fontSize: "20px !important" }}>menu_book</Icon>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: palette.blue[700] }}>{t("bulkUpload.labels.instructions")}</MDTypography>
              </MDBox>
              <InstructionList color={palette.blue[700]} items={[
                t("bulkUpload.instructions.instruction1"),
                t("bulkUpload.instructions.instruction2"),
                t("bulkUpload.instructions.instruction3"),
                t("bulkUpload.instructions.instruction4"),
                t("bulkUpload.instructions.instruction6"),
                t("bulkUpload.instructions.instruction7"),
                t("bulkUpload.instructions.instruction5"),
              ]} />
              {renderCoachRules(true)}
            </MDBox>
          </MDBox>
          {/* pinned Next button */}
          <MDBox sx={{ flexShrink: 0, display: "flex", justifyContent: "flex-end", pt: 2 }}>
            <MDButton variant="contained" onClick={() => setStep(3)} sx={{ borderRadius: "12px", px: 4, py: 1.25, textTransform: "none", fontWeight: "bold", bgcolor: palette.brand[500], color: "#fff", "&:hover": { bgcolor: palette.brand[600] } }}>
              <Icon sx={{ mr: 0.75, fontSize: "18px !important" }}>arrow_forward</Icon>
              {t("buttonTitles.next")}
            </MDButton>
          </MDBox>
        </MDBox>
      )}

      {/* ── Step 12: manual entry instructions ── */}
      {step === 12 && (
        <MDBox sx={{ px: 4, pt: 5, pb: 4, display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
          {/* pinned header */}
          <MDBox sx={{ flexShrink: 0 }}>
            <StepHeader icon="edit_note" iconColor={palette.brand[500]} iconBg={`${palette.brand[500]}18`} title={t("bulkUpload.headings.addChildrenDetails")} subtitle={adding === "asParent" ? t("messages.profile.addAsParent") : t("messages.profile.addAsCoach")} />
          </MDBox>
          {/* scrollable instruction content */}
          <MDBox sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
            <MDBox sx={{ bgcolor: palette.brand[50], borderRadius: "16px", p: 3 }}>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <Icon sx={{ color: palette.blue[700], fontSize: "20px !important" }}>menu_book</Icon>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: palette.blue[700] }}>{t("bulkUpload.labels.instructions")}</MDTypography>
              </MDBox>
              <InstructionList color={palette.blue[700]} items={[
                t("bulkUpload.instructions.instruction3"),
                t("bulkUpload.instructions.instruction7"),
                t("bulkUpload.instructions.instruction5"),
              ]} />
              {renderCoachRules(false)}
            </MDBox>
          </MDBox>
          {/* pinned Next button */}
          <MDBox sx={{ flexShrink: 0, display: "flex", justifyContent: "flex-end", pt: 2 }}>
            <MDButton variant="contained" onClick={() => setStep(4)} sx={{ borderRadius: "12px", px: 4, py: 1.25, textTransform: "none", fontWeight: "bold", bgcolor: palette.brand[500], color: "#fff", "&:hover": { bgcolor: palette.brand[600] } }}>
              <Icon sx={{ mr: 0.75, fontSize: "18px !important" }}>arrow_forward</Icon>
              {t("buttonTitles.next")}
            </MDButton>
          </MDBox>
        </MDBox>
      )}

      {/* ── Step 3: bulk upload ── */}
      {step === 3 && (
        <MDBox sx={{ px: 4, pt: 5, pb: 4, display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
          {/* pinned top: header + action buttons */}
          <MDBox sx={{ flexShrink: 0 }}>
            <StepHeader icon="upload_file" iconColor="#ff9800" iconBg="#ff980018" title={t("headings.addLearner")} />
            <Stack spacing={2} mb={previewData.length > 0 ? 2 : 0}>
              <MDButton variant="outlined" component="a" href={adding === "asCoach" ? "/assets/learners_data_coach.xlsx" : "/assets/learners_data_parent.xlsx"} download fullWidth
                sx={{ borderRadius: "12px", py: 1.5, textTransform: "none", fontWeight: "bold", borderColor: palette.brand[300], color: palette.brand[500], "&:hover": { borderColor: palette.brand[500], bgcolor: `${palette.brand[500]}08` } }}>
                <Icon sx={{ mr: 1, fontSize: "20px !important" }}>download</Icon>
                {t("buttonTitles.downloadTemplate")}
              </MDButton>
              <MDButton variant="contained" component="label" fullWidth disabled={uploading}
                sx={{ borderRadius: "12px", py: 1.5, textTransform: "none", fontWeight: "bold", bgcolor: palette.brand[500], color: "#fff", "&:hover": { bgcolor: palette.brand[600] }, "&.Mui-disabled": { bgcolor: palette.neutral[200] } }}>
                <Icon sx={{ mr: 1, fontSize: "20px !important" }}>{uploading ? "hourglass_empty" : "upload"}</Icon>
                {uploading ? t("buttonTitles.uploading") : t("buttonTitles.uploadFile")}
                <input type="file" disabled={uploading} hidden ref={fileInputRef} accept=".xlsx" onChange={handleUpload} />
              </MDButton>
            </Stack>
          </MDBox>

          {/* named duplicate report: which addresses collided, and on which rows */}
          {duplicateEmails.length > 0 && (
            <MDBox sx={{ flexShrink: 0, mt: 2, p: 2, borderRadius: "12px", bgcolor: "#fdecea", border: "1px solid #f4433633" }}>
              <MDBox display="flex" alignItems="center" gap={1} mb={0.75}>
                <Icon sx={{ color: "#c62828", fontSize: "20px !important" }}>error_outline</Icon>
                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#c62828" }}>
                  {t("bulkUpload.headings.duplicateEmails") || "Duplicate email addresses"}
                </MDTypography>
              </MDBox>
              <MDTypography variant="caption" color="text.secondary" display="block" mb={1.5}>
                {t("bulkUpload.messages.duplicateEmails") || "Correct these rows in your spreadsheet and upload it again. Nothing has been created yet."}
              </MDTypography>
              <Stack spacing={1} sx={{ maxHeight: 200, overflowY: "auto", pr: 0.5 }}>
                {duplicateEmails.map((item, i) => (
                  <MDBox key={i} sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#fff", borderLeft: "4px solid #f44336" }}>
                    <MDTypography variant="body2" fontWeight="bold" sx={{ wordBreak: "break-all" }}>{item.email}</MDTypography>
                    <MDTypography variant="caption" color="text.secondary" display="block">
                      {t("bulkUpload.labels.spreadsheetRows") || "Spreadsheet rows"}: {item.rows.join(", ")}
                    </MDTypography>
                    <MDBox mt={0.5} display="inline-block" px={1.5} py={0.2} borderRadius="8px" sx={{ bgcolor: "#fdecea", color: "#c62828", fontWeight: 700, fontSize: "0.68rem" }}>
                      {item.reason === "sameRow"
                        ? (t("bulkUpload.labels.duplicateSameRow") || "CHILD AND PARENT EMAIL ARE IDENTICAL")
                        : item.reason === "sharedParent"
                          ? (t("bulkUpload.labels.duplicateSharedParent") || "PARENT EMAIL REUSED")
                          : (t("bulkUpload.labels.duplicateRepeated") || "SAME EMAIL ON MULTIPLE ROWS")}
                    </MDBox>
                    {item.reason === "sharedParent" && (
                      <MDTypography variant="caption" color="text.secondary" display="block" mt={0.75}>
                        {t("bulkUpload.messages.duplicateSharedParentHint") || "A shared account uses the parent's email as the login, so it can only hold one child. To add siblings under this parent, give each child their own email address."}
                      </MDTypography>
                    )}
                  </MDBox>
                ))}
              </Stack>
            </MDBox>
          )}

          {/* preview section: table scrolls, terms + submit pinned at bottom */}
          {previewData.length > 0 && (
            <MDBox sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <MDTypography variant="subtitle2" fontWeight="bold" mb={1} textAlign="center">{t("bulkUpload.headings.preview")}</MDTypography>
              {/* scrollable table */}
              <MDBox sx={{ flex: 1, minHeight: 0, border: `1px solid ${palette.neutral[200]}`, borderRadius: "12px", mb: 2,overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["first_name", "last_name", "gender", "dob_display", "email", ...(adding === "asCoach" ? ["parent_email"] : []), "code", "user_age"].map((key) => (
                        <th key={key} style={{ border: "1px solid #e0e0e0", padding: "8px 12px", background: palette.neutral[100], fontSize: "0.72rem", fontWeight: 700, textAlign: "left", whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 1 }}>
                          {key.replace(/_/g, " ")}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : palette.neutral[50] }}>
                        {["first_name", "last_name", "gender", "dob_display", "email", ...(adding === "asCoach" ? ["parent_email"] : []), "code", "user_age"].map((key) => (
                          <td key={key} style={{ border: "1px solid #e0e0e0", padding: "6px 12px", fontSize: "0.8rem" }}>{row[key]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </MDBox>
              {/* pinned bottom: terms + submit */}
              <MDBox sx={{ flexShrink: 0 }}>
                <form onSubmit={handleSubmit}>
                  {renderTerms()}
                  <MDButton variant="contained" type="submit" fullWidth sx={{ borderRadius: "12px", py: 1.5, textTransform: "none", fontWeight: "bold", bgcolor: palette.brand[500], color: "#fff", "&:hover": { bgcolor: palette.brand[600] } }}>
                    <Icon sx={{ mr: 0.75, fontSize: "18px !important" }}>check_circle</Icon>
                    {t("buttonTitles.submitLearners")}
                  </MDButton>
                </form>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      )}

      {/* ── Step 4: manual entry ── */}
      {step === 4 && (
        <MDBox sx={{ px: 4, pt: 5, pb: 4, display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
          <MDBox sx={{ flexShrink: 0 }}>
            <StepHeader icon="edit_note" iconColor={palette.brand[500]} iconBg={`${palette.brand[500]}18`} title={t("bulkUpload.headings.addChildrenDetails")} subtitle={adding === "asParent" ? t("messages.profile.addAsParent") : t("messages.profile.addAsCoach")} />
          </MDBox>
          {/* scrollable learner cards — grows to fill space, footer stays pinned */}
          <MDBox sx={{ flex: 1, overflowY: "auto", minHeight: 0, pr: 0.5 }}>
            {manualLearners.map((learner, index) => {
              const learnerAge = getLearnerAge(learner.dob);
              const isMinor = learnerAge !== null && learnerAge < 18;
              const isAdult = learnerAge !== null && learnerAge >= 18;
              const showCoachEmails = adding === "asCoach" && learner.dob;
              const emailsMatch = learner.email?.trim() && learner.parent_email?.trim() && learner.email.trim().toLowerCase() === learner.parent_email.trim().toLowerCase();
              return (
                <Card key={index} sx={{ p: 2.5, mb: 2, borderRadius: "16px", border: `1px solid ${palette.neutral[200]}`, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDBox sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: `${palette.brand[500]}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: palette.brand[500] }}>{index + 1}</MDTypography>
                      </MDBox>
                    </MDBox>
                    {index > 0 && (
                      <MDBox onClick={() => removeManualRow(index)} sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "#fdecea", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { bgcolor: "#f44336", "& .MuiIcon-root": { color: "#fff" } } }}>
                        <Icon sx={{ fontSize: "16px !important", color: "#f44336" }}>delete</Icon>
                      </MDBox>
                    )}
                  </MDBox>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6} md={3}>
                      <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}><span style={{ color: "#f44336" }}>*</span> {t("formLabels.firstName")}</MDTypography>
                      <TextField fullWidth size="small" value={learner.first_name} onChange={(e) => handleManualChange(index, "first_name", e.target.value)} sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}><span style={{ color: "#f44336" }}>*</span> {t("formLabels.lastName")}</MDTypography>
                      <TextField fullWidth size="small" value={learner.last_name} onChange={(e) => handleManualChange(index, "last_name", e.target.value)} sx={fieldSx} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}><span style={{ color: "#f44336" }}>*</span> {t("formLabels.gender")}</MDTypography>
                      <FormControl fullWidth size="small" sx={fieldSx}>
                        <Select sx= {{ height: "35px" }} value={learner.gender} onChange={(e) => handleManualChange(index, "gender", e.target.value)} displayEmpty>
                          <MenuItem value="" disabled><em style={{ color: palette.neutral[400] }}>Select</em></MenuItem>
                          <MenuItem value="Male">{t("formLabels.male")}</MenuItem>
                          <MenuItem value="Female">{t("formLabels.female")}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}><span style={{ color: "#f44336" }}>*</span> {t("formLabels.birthdate")}</MDTypography>
                      <TextField type="date" size="small" fullWidth value={learner.dob} onChange={(e) => handleDobChange(index, e.target.value)} inputProps={{ min: startDate, max: endDate }} sx={fieldSx} />
                    </Grid>

                    {adding === "asParent" && (
                      <Grid item xs={12} sm={6} md={3}>
                        <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}><span style={{ color: "#f44336" }}>*</span> {t("formLabels.email")}</MDTypography>
                        <TextField fullWidth size="small" value={learner.email} onChange={(e) => handleManualChange(index, "email", e.target.value)} sx={fieldSx} />
                      </Grid>
                    )}
                    {showCoachEmails && isMinor && (
                      <>
                        <Grid item xs={12} sm={6} md={4}>
                          <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>{t("formLabels.learnerEmail")}</MDTypography>
                          <TextField fullWidth size="small" value={learner.email} error={!!emailsMatch} helperText={emailsMatch ? t("formLabels.emailsMustBeDifferent") : ""} onChange={(e) => handleManualChange(index, "email", e.target.value)} sx={fieldSx} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>{t("formLabels.parentEmail")}</MDTypography>
                          <TextField fullWidth size="small" value={learner.parent_email} error={!!emailsMatch} helperText={emailsMatch ? t("formLabels.emailsMustBeDifferent") : ""} onChange={(e) => handleManualChange(index, "parent_email", e.target.value)} sx={fieldSx} />
                        </Grid>
                      </>
                    )}
                    {showCoachEmails && isAdult && (
                      <Grid item xs={12} sm={6} md={4}>
                        <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}><span style={{ color: "#f44336" }}>*</span> {t("formLabels.email")}</MDTypography>
                        <TextField fullWidth size="small" value={learner.email} onChange={(e) => handleManualChange(index, "email", e.target.value)} sx={fieldSx} />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={2}>
                      <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>{t("formLabels.code")}</MDTypography>
                      <TextField fullWidth size="small" value={learner.code} onChange={(e) => handleManualChange(index, "code", e.target.value.trim())} sx={fieldSx} />
                    </Grid>
                  </Grid>
                </Card>
              );
            })}
          </MDBox>

          {/* pinned footer — always visible */}
          <MDBox sx={{ flexShrink: 0, pt: 1 }}>
            {manualLearners.length < 100 && (
              <MDBox display="flex" justifyContent="flex-end" mb={2}>
                <MDButton variant="outlined" onClick={addManualRow} sx={{ borderRadius: "12px", px: 3, py: 1, textTransform: "none", fontWeight: "bold", borderColor: palette.brand[300], color: palette.brand[500], "&:hover": { borderColor: palette.brand[500], bgcolor: `${palette.brand[500]}08` } }}>
                  <Icon sx={{ mr: 0.75, fontSize: "18px !important" }}>add</Icon>
                  {t("buttonTitles.addAthlete")}
                </MDButton>
              </MDBox>
            )}

            {renderTerms()}

            <MDButton variant="contained" fullWidth onClick={handleManualSubmit} sx={{ borderRadius: "12px", py: 1.5, textTransform: "none", fontWeight: "bold", bgcolor: palette.brand[500], color: "#fff", "&:hover": { bgcolor: palette.brand[600] } }}>
              <Icon sx={{ mr: 0.75, fontSize: "18px !important" }}>check_circle</Icon>
              {t("buttonTitles.submitAthletes")}
            </MDButton>
          </MDBox>
        </MDBox>
      )}

      {/* ── Step 5: payment summary ── */}
      {step === 5 && (
        <MDBox sx={{ px: 4, pt: 5, pb: 4, display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
          {/* pinned top: header + avg cost */}
          <MDBox sx={{ flexShrink: 0 }}>
            <StepHeader icon="receipt_long" iconColor="#4caf50" iconBg="#4caf5018" title={t("bulkUpload.headings.paymentSummary")} />
            <MDBox display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ bgcolor: palette.neutral[50], borderRadius: "12px", p: 2, mb: 2 }}>
              <MDTypography variant="h6">{t("bulkUpload.headings.averageCost")}</MDTypography>
              <MDTypography variant="h6" fontWeight="bold" sx={{ color: palette.brand[500] }}>${averageCost}</MDTypography>
            </MDBox>
          </MDBox>

          {/* scrollable learner list */}
          <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5, mb: 2 }}>
            {learnerDetails.map((item, i) => (
              <MDBox key={i} sx={{ p: 2, borderRadius: "12px", bgcolor: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", borderLeft: `4px solid ${item.code_status === "valid_code" ? "#4caf50" : item.code_status === "limit_exceeded" ? palette.amber[600] : palette.brand[200]}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <MDBox>
                  <MDTypography variant="body2" fontWeight="bold">{item.name}</MDTypography>
                  <MDTypography variant="caption" color="text.secondary" display="block">{t("formLabels.email")}: {item.email}</MDTypography>
                  <MDTypography variant="caption" color="text.secondary" display="block">{t("formLabels.code")}: {item.referral_code || "—"}</MDTypography>
                  <MDBox mt={0.75} display="inline-block" px={1.5} py={0.2} borderRadius="8px" sx={{ bgcolor: item.code_status === "valid_code" ? "#e8f5e9" : item.code_status === "limit_exceeded" ? palette.amber[50] : "#fdecea", color: item.code_status === "valid_code" ? "#2e7d32" : item.code_status === "limit_exceeded" ? palette.amber[800] : "#c62828", fontWeight: 700, fontSize: "0.68rem" }}>
                    {item.code_status.replaceAll("_", " ").toUpperCase()}
                  </MDBox>
                </MDBox>
                <MDTypography variant="h6" fontWeight="bold">${item.price}</MDTypography>
              </MDBox>
            ))}
            {existingLearners.map((item, i) => (
              <MDBox key={i} sx={{ p: 2, borderRadius: "12px", bgcolor: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", borderLeft: "4px solid #f44336" }}>
                <MDTypography variant="body2" fontWeight="bold">{item.name}</MDTypography>
                <MDTypography variant="caption" color="text.secondary" display="block">{t("formLabels.email")}: {item.email}</MDTypography>
                <MDBox mt={0.75} display="inline-block" px={1.5} py={0.2} borderRadius="8px" sx={{ bgcolor: "#fdecea", color: "#c62828", fontWeight: 700, fontSize: "0.68rem" }}>
                  {t("bulkUpload.labels.userExisted")}
                </MDBox>
              </MDBox>
            ))}
            {parentAccounts.length > 0 && (
              <>
                <MDTypography variant="subtitle2" fontWeight="bold" mt={0.5}>{t("bulkUpload.labels.parentAccounts") || "Parent Accounts"}</MDTypography>
                {parentAccounts.map((item, i) => (
                  <MDBox key={i} sx={{ p: 2, borderRadius: "12px", bgcolor: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", borderLeft: "4px solid #1976d2" }}>
                    <MDTypography variant="caption" color="text.secondary" display="block">{t("formLabels.email")}: {item.email}</MDTypography>
                    <MDBox mt={0.75} display="inline-block" px={1.5} py={0.2} borderRadius="8px" sx={{ bgcolor: item.status === "created" ? "#e3f2fd" : "#f3e5f5", color: item.status === "created" ? "#1565c0" : "#6a1b9a", fontWeight: 700, fontSize: "0.68rem" }}>
                      {item.status === "created" ? (t("bulkUpload.labels.parentAccountCreated") || "PARENT ACCOUNT CREATED") : (t("bulkUpload.labels.parentAccountExisting") || "EXISTING PARENT ACCOUNT")}
                    </MDBox>
                    <MDTypography variant="caption" color="text.secondary" display="block" mt={0.5}>{t("bulkUpload.labels.parentAccountNote") || "No payment required for parent account."}</MDTypography>
                  </MDBox>
                ))}
              </>
            )}
          </Stack>

          {/* pinned bottom: total + proceed button */}
          <MDBox sx={{ flexShrink: 0 }}>
            <MDBox sx={{ bgcolor: palette.neutral[50], borderRadius: "12px", p: 2, mb: 2, textAlign: "center" }}>
              <MDTypography variant="h6" fontWeight="bold">
                {t("bulkUpload.labels.totalAmount")} <span style={{ color: palette.brand[500] }}>${amountToProcess}</span>
              </MDTypography>
            </MDBox>
            <MDButton variant="contained" fullWidth onClick={proceedToPayment} sx={{ borderRadius: "12px", py: 1.5, textTransform: "none", fontWeight: "bold", bgcolor: palette.brand[500], color: "#fff", "&:hover": { bgcolor: palette.brand[600] } }}>
              <Icon sx={{ mr: 0.75, fontSize: "18px !important" }}>{noLearners ? "close" : noPaymentRequired ? "check_circle" : "payment"}</Icon>
              {noLearners ? t("buttonTitles.close") : noPaymentRequired ? t("buttonTitles.proceedToFinish") : t("buttonTitles.payNow")}
            </MDButton>
          </MDBox>
        </MDBox>
      )}

      {/* ── Step 6: bulk payment ── */}
      {step === 6 && (
        <MDBox sx={{ px: 4, pt: 5, pb: 4, display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
          <MDBox textAlign="center" mb={3}>
              <MDBox
                sx={{
                  width: 80, height: 80, borderRadius: "50%",
                  bgcolor: "#4caf5018",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  mx: "auto", mb: 2,
                }}
              >
                <Icon sx={{ fontSize: "40px !important", color: "#4caf50" }}>credit_card</Icon>
              </MDBox>
              <MDTypography variant="h5" fontWeight="bold" mb={0.5}>
                {t("headings.maxuPayment")}
              </MDTypography>
              <MDTypography variant="body2" color="text.secondary">
                {t(`messages.subscription.maxuAnnualFeeBulk`, { amount: averageCost })}
              </MDTypography>
            </MDBox>



          {/* scrollable stripe form — email + card + billing address + pay now */}
          <MDBox sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
            <BulkPayment learnerDetails={learnerDetails} amountToProcess={amountToProcess} averageCost={averageCost} subscriptionType={subscriptionType} setStep={setStep} getChildrens={getChildrens} updateUserProfile={updateUserProfile} setOpenSnackbar={setOpenSnackbar} setAlertMessage={setAlertMessage} setSeverity={setSeverity} />
          </MDBox>
        </MDBox>
      )}

      {/* ── Step 8: payment completed ── */}
      {step === 8 && (
        <MDBox sx={{ px: 4, pt: 5, pb: 4, textAlign: "center" }}>
          <MDBox sx={{ width: 100, height: 100, borderRadius: "50%", bgcolor: "#e8f5e9", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 3 }}>
            <Icon sx={{ fontSize: "52px !important", color: "#4caf50" }}>check_circle</Icon>
          </MDBox>
          <MDTypography variant="h5" fontWeight="bold" mb={4}>{t("messages.subscription.paymentCompleted")}</MDTypography>
          <MDButton variant="contained" fullWidth onClick={() => setStep(9)} sx={{ borderRadius: "12px", py: 1.5, textTransform: "none", fontWeight: "bold", bgcolor: palette.brand[500], color: "#fff", "&:hover": { bgcolor: palette.brand[600] } }}>
            {t("buttonTitles.finish")}
          </MDButton>
        </MDBox>
      )}

      {/* ── Step 9: thank you / instructions ── */}
      {step === 9 && (
        <MDBox sx={{ px: 4, pt: 5, pb: 4 }}>
          <MDBox textAlign="center" mb={3}>
            <MDBox sx={{ width: 80, height: 80, borderRadius: "50%", bgcolor: "#4caf5018", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
              <Icon sx={{ fontSize: "40px !important", color: "#4caf50" }}>how_to_reg</Icon>
            </MDBox>
          </MDBox>
          <Typography variant="body1" fontSize={15} sx={{ whiteSpace: "normal", lineHeight: 1.8, mb: 3 }} dangerouslySetInnerHTML={{ __html: t("messages.subscription.thankyouMessageChild") }} />
          <MDBox display="flex" gap={2}>
            <MDButton variant="outlined" fullWidth onClick={handleSignupChild} sx={{ borderRadius: "12px", py: 1.5, textTransform: "none", fontWeight: "bold", borderColor: "#ff9800", color: "#ff9800", "&:hover": { bgcolor: "#ff980010" } }}>
              {t("buttonTitles.signupChild")}
            </MDButton>
            <MDButton variant="contained" fullWidth onClick={handleCloseBulkUploadModal} sx={{ borderRadius: "12px", py: 1.5, textTransform: "none", fontWeight: "bold", bgcolor: palette.brand[500], color: "#fff", "&:hover": { bgcolor: palette.brand[600] } }}>
              {t("buttonTitles.close")}
            </MDButton>
          </MDBox>
        </MDBox>
      )}

      {/* ── Step 10: loading ── */}
      {step === 10 && (
        <MDBox sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300, p: 4 }}>
          <Loader loaderText={t("loaderText.bulkAdd")} />
        </MDBox>
      )}
    </Dialog>
  );
};

export default BulkUpload;
